import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export interface CreateOption {
  // The raw trigger id stored on the script (spot ueIdentifier or AI trigger
  // region name).
  id: string;
  label: string;
  sublabel?: string;
}

interface ICreateEntityPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  title: string;
  placeholder: string;
  // Full list of pickable spots/areas for the active mode.
  options: CreateOption[];
  // The trigger UE is currently reporting (AI trigger mode) — surfaced as a
  // "current" quick pick at the top.
  liveOption?: CreateOption | null;
  onClose: () => void;
  onPick: (region: string) => void;
}

const usePopoverStyles = () => ({
  paper: {
    width: 300,
    background:
      'linear-gradient(135deg, rgba(32, 32, 40, 0.97), rgba(18, 18, 24, 0.97))',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255, 255, 255, 0.13)',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.6)',
    padding: '12px',
    color: '#e9eaf1',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(440px, 70vh)',
  },
  title: {
    fontSize: '12px',
    fontWeight: 750,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '9px',
    color: '#e9eaf1',
    flex: 'none',
    '& > svg': {
      color: '#b9aef8',
      fontSize: 14,
    },
  },
  closeBtn: {
    color: '#969aa8',
    padding: '2px',
    marginLeft: 'auto',
  },
  search: {
    flex: 'none',
    marginBottom: '6px',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.34)',
      borderRadius: '9px',
      color: '#e9eaf1',
      fontSize: '12px',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.055)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(110, 108, 246, 0.5)' },
    },
    '& .MuiOutlinedInput-input': { padding: '7px 4px' },
  },
  searchIcon: {
    color: '#7e8290',
    fontSize: 16,
    marginRight: '2px',
  },
  sectionLabel: {
    fontSize: '9.5px',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#808592',
    margin: '8px 4px 4px',
    flex: 'none',
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    margin: '0 -4px',
    padding: '0 4px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#dfe1e9',
    borderRadius: '9px',
    padding: '7px 8px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
    '& > svg': { color: '#969aa8', fontSize: 14, flex: 'none' },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  rowLabel: {
    fontSize: '12px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowSub: {
    fontFamily: MONO,
    fontSize: '10px',
    color: '#808592',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowTag: {
    marginLeft: 'auto',
    fontSize: '9.5px',
    color: '#8d92a0',
    fontWeight: 700,
    letterSpacing: '0.04em',
    flex: 'none',
  },
  empty: {
    fontSize: '11px',
    color: '#808592',
    fontStyle: 'italic',
    padding: '10px 8px',
    textAlign: 'center' as const,
  },
  customRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    marginTop: '4px',
    border: '1px dashed rgba(255, 255, 255, 0.2)',
    background: 'transparent',
    color: '#dadcf8',
    borderRadius: '9px',
    padding: '8px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    fontSize: '11.5px',
    fontWeight: 650,
    flex: 'none',
    '&:hover': {
      borderColor: 'rgba(110, 108, 246, 0.5)',
      backgroundColor: 'rgba(110, 108, 246, 0.08)',
    },
    '& > svg': { color: '#b9aef8', fontSize: 14, flex: 'none' },
    '& code': {
      fontFamily: MONO,
      color: '#e9eaf1',
    },
  },
});

const CreateEntityPopover = ({
  open,
  anchorEl,
  title,
  placeholder,
  options,
  liveOption,
  onClose,
  onPick,
}: ICreateEntityPopoverProps) => {
  const { t: translate } = useTranslation();
  const classes = usePopoverStyles();
  const [query, setQuery] = useState('');

  const handleClose = useCallback(() => {
    setQuery('');
    onClose();
  }, [onClose]);

  // Esc closes the popover. Popper itself doesn't manage keyboard.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const handlePick = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }
      onPick(trimmed);
      setQuery('');
    },
    [onPick],
  );

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) {
      return options;
    }
    return options.filter(
      (option) =>
        option.id.toLowerCase().includes(q) ||
        option.label.toLowerCase().includes(q) ||
        (option.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, trimmedQuery]);

  // Offer a free-text fallback only when the query doesn't already match an
  // existing option exactly.
  const showCustom =
    !!trimmedQuery &&
    !options.some(
      (option) =>
        option.id.toLowerCase() === trimmedQuery.toLowerCase() ||
        option.label.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  const showLive =
    !!liveOption &&
    (!trimmedQuery ||
      liveOption.label.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
      liveOption.id.toLowerCase().includes(trimmedQuery.toLowerCase()));

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 12 } },
      ]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={classes.paper} role="dialog" aria-modal="false">
          <Box sx={classes.title}>
            <LocationOnOutlinedIcon />
            {title}
            <IconButton
              size="small"
              sx={classes.closeBtn}
              onClick={handleClose}
              aria-label={translate('editorMode.close', 'Close')}
              data-testid="ai-create-popover-close-btn"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            size="small"
            sx={classes.search}
            placeholder={placeholder}
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered.length === 1) {
                  handlePick(filtered[0].id);
                } else if (showCustom) {
                  handlePick(trimmedQuery);
                }
              } else if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            InputProps={{
              startAdornment: <SearchOutlinedIcon sx={classes.searchIcon} />,
            }}
            inputProps={{ 'data-testid': 'ai-create-popover-search-input' }}
          />

          {showLive ? (
            <>
              <Typography sx={classes.sectionLabel}>
                {translate('editorMode.aiCreateYourPosition')}
              </Typography>
              <Box
                component="button"
                type="button"
                sx={classes.row}
                onClick={() => handlePick((liveOption as CreateOption).id)}
                data-testid="ai-create-popover-live-option"
              >
                <LocationOnOutlinedIcon />
                <Box sx={classes.rowText}>
                  <Box component="span" sx={classes.rowLabel}>
                    {(liveOption as CreateOption).label}
                  </Box>
                </Box>
                <Box component="span" sx={classes.rowTag}>
                  {translate('editorMode.aiCreateCurrent')}
                </Box>
              </Box>
            </>
          ) : null}

          <Typography sx={classes.sectionLabel}>
            {translate('editorMode.aiCreateAvailable')}
            {options.length > 0 ? ` (${options.length})` : ''}
          </Typography>

          <Box sx={classes.list}>
            {filtered.length === 0 && !showCustom ? (
              <Typography sx={classes.empty}>
                {options.length === 0
                  ? translate('editorMode.aiCreateNoOptions')
                  : translate('editorMode.aiCreateNoMatches')}
              </Typography>
            ) : (
              filtered.map((option) => (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  sx={classes.row}
                  onClick={() => handlePick(option.id)}
                  data-testid={`ai-create-popover-option-${option.id}`}
                >
                  <LocationOnOutlinedIcon />
                  <Box sx={classes.rowText}>
                    <Box component="span" sx={classes.rowLabel}>
                      {option.label}
                    </Box>
                    {option.sublabel ? (
                      <Box component="span" sx={classes.rowSub}>
                        {option.sublabel}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {showCustom ? (
            <Box
              component="button"
              type="button"
              sx={classes.customRow}
              onClick={() => handlePick(trimmedQuery)}
              data-testid="ai-create-popover-custom-option"
            >
              <LocationOnOutlinedIcon />
              {translate('editorMode.aiCreateUseCustom')}{' '}
              <Box component="code">{trimmedQuery}</Box>
            </Box>
          ) : null}
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default CreateEntityPopover;
